import { getBlockType } from './block-utils';
import { extractFromParagraphText } from './extractFromParagraphText';
import { extractParagraphsFromTable } from './extractParagraphsFromTable';
import { formatListMarker } from './list-utils';
import type { AlineContentBlock, ListMarkerStyleName } from '../../slate-api';
import {
  dynamicBlockType,
  instructionFieldElementType,
  paragraphType,
  tableType,
} from '../../slate-api';
import { assertNeverThrow } from '../../util/ts-utils';
import type { Context, ExtractedParagraph } from '../shared/types';

type ListState = {
  listIndexes: number[];
  listStyles: (ListMarkerStyleName | undefined)[];
  previousListLevel: number;
};

export function extractParagraphsFromContentBlocks(
  children: AlineContentBlock[],
  context: Context
): ExtractedParagraph[] {
  const paragraphs: ExtractedParagraph[] = [];
  const listState: ListState = {
    listIndexes: [],
    listStyles: [],
    previousListLevel: -1,
  };

  for (const [blockIndex, block] of children.entries()) {
    const blockPath = [...context.path, blockIndex];
    switch (block.type) {
      case tableType: {
        paragraphs.push(...extractParagraphsFromTable(block, { ...context, path: blockPath }));
        break;
      }
      case paragraphType: {
        const paragraph = block;
        const { fontSize, segments } = extractFromParagraphText(paragraph);
        const { listLevel, styleName, type } = getBlockType(paragraph, context.listDefinitions);

        let listMarker = '';

        switch (type) {
          case 'NUMBERED': {
            // Reset deeper levels when going to a shallower level
            if (listLevel <= listState.previousListLevel) {
              listState.listIndexes.length = listLevel + 1;
              listState.listStyles.length = listLevel + 1;
            }

            // Initialize or increment the index at current level
            if (listState.listIndexes[listLevel] === undefined) {
              listState.listIndexes[listLevel] = 0;
            } else {
              listState.listIndexes[listLevel]++;
            }
            listState.listStyles[listLevel] = styleName;

            // Build dotted marker from all levels
            const parts: string[] = [];
            for (let i = 0; i <= listLevel; i++) {
              if (listState.listIndexes[i] !== undefined) {
                parts.push(formatListMarker(listState.listIndexes[i], listState.listStyles[i]));
              }
            }
            listMarker = parts.join('.') + '.';

            listState.previousListLevel = listLevel;
            break;
          }
          case 'P': {
            // Non-list paragraph resets tracking
            listState.listIndexes.length = 0;
            listState.listStyles.length = 0;
            listState.previousListLevel = -1;
            break;
          }
          case 'BULLET':
            listMarker = '*.'.repeat(listLevel + 1);
            break;
          default:
            // We should handle all cases so throw on failure to do so.
            assertNeverThrow(type);
        }

        paragraphs.push({
          cellRanges: context.cellPath,
          fontSize,
          // Convert to 1-based for lists, keep 0 for non-list paragraphs
          listLevel: type === 'P' ? 0 : listLevel + 1,
          listMarker,
          locationAnchor: paragraph.locationId ?? '',
          path: blockPath,
          section: context.section,
          segments,
          source: 'Aline',
          type,
        });
        break;
      }
      case dynamicBlockType:
      case instructionFieldElementType:
        paragraphs.push(
          ...extractParagraphsFromContentBlocks(block.children, { ...context, path: blockPath })
        );
        break;
      default:
        assertNeverThrow(block);
    }
  }

  return paragraphs;
}
